import type { ExperienceTrackerAPI } from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';
import { remixCreateTerminalOwnership } from './remix-create-terminal-ownership';

export const REMIX_INSERT_RENDER_TIMEOUT_MS = 35_000;
export const REMIX_DIAGRAM_RENDER_TIMEOUT_MS = 45_000;
export const REMIX_INSERT_OBSERVATION_LIMIT_MS = 120_000;
const RETAINED_RENDER_OUTCOME_TIMEOUT_MS = 5 * 60_000;

export type RemixInsertRenderIdentity = {
	type: 'localId' | 'mediaId';
	value: string;
};

type RenderOutcome = { type: 'succeeded' } | { error: Error; reason: string; type: 'failed' };

type PendingVerification = {
	attributes?: Record<string, unknown>;
	deadlineExceeded?: boolean;
	experienceTracker: ExperienceTrackerAPI;
	observationTimeout?: ReturnType<typeof setTimeout>;
	renderTimeoutMs: number;
	timeout?: ReturnType<typeof setTimeout>;
	unsubscribe?: () => void;
};

const pendingVerifications = new Map<string, PendingVerification>();
const recentOutcomes = new Map<
	string,
	{ outcome: RenderOutcome; timeout?: ReturnType<typeof setTimeout> }
>();
const retainedRenderIdentities = new Map<string, ReturnType<typeof setTimeout>>();

const toKey = ({ type, value }: RemixInsertRenderIdentity): string => `${type}:${value}`;

const clearRetainedIdentity = (key: string): void => {
	const timeout = retainedRenderIdentities.get(key);
	if (timeout !== undefined) {
		clearTimeout(timeout);
		retainedRenderIdentities.delete(key);
	}
};

export const retainRemixInsertRenderOutcome = (identity: RemixInsertRenderIdentity): void => {
	const key = toKey(identity);
	clearRetainedIdentity(key);
	const recent = recentOutcomes.get(key);
	if (recent?.timeout !== undefined && recent.outcome.type === 'succeeded') {
		clearTimeout(recent.timeout);
		recentOutcomes.set(key, { outcome: recent.outcome });
	}
	retainedRenderIdentities.set(
		key,
		setTimeout(() => {
			retainedRenderIdentities.delete(key);
			recentOutcomes.delete(key);
		}, RETAINED_RENDER_OUTCOME_TIMEOUT_MS),
	);
};

export const releaseRemixInsertRenderOutcome = (identity: RemixInsertRenderIdentity): void => {
	const key = toKey(identity);
	clearRetainedIdentity(key);
	const recent = recentOutcomes.get(key);
	if (recent?.timeout !== undefined) {
		clearTimeout(recent.timeout);
	}
	recentOutcomes.delete(key);
};

const stopObserving = (pending: PendingVerification): void => {
	clearTimeout(pending.timeout);
	clearTimeout(pending.observationTimeout);
	pending.unsubscribe?.();
};

export const hasPendingRemixInsertRenderVerification = (
	identity: RemixInsertRenderIdentity,
): boolean => pendingVerifications.has(toKey(identity));

const settle = (pending: PendingVerification, outcome: RenderOutcome): void => {
	stopObserving(pending);
	if (!pending.deadlineExceeded) {
		try {
			if (outcome.type === 'succeeded') {
				pending.experienceTracker.succeed({
					name: ExperienceName.REMIX_INSERT_LIMITED,
					attributes: { ...pending.attributes, renderDeadlineMs: pending.renderTimeoutMs },
				});
			} else {
				pending.experienceTracker.fail({
					name: ExperienceName.REMIX_INSERT_LIMITED,
					error: outcome.error,
					attributes: {
						...pending.attributes,
						renderDeadlineMs: pending.renderTimeoutMs,
						renderFailureReason: outcome.reason,
					},
				});
			}
		} catch {}
	}
	try {
		const attributes = {
			...pending.attributes,
			renderDeadlineMs: pending.renderTimeoutMs,
			deadlineExceeded: !!pending.deadlineExceeded,
		};
		if (outcome.type === 'succeeded') {
			pending.experienceTracker.succeed({
				name: ExperienceName.REMIX_INSERT,
				attributes,
			});
			return;
		}

		pending.experienceTracker.fail({
			name: ExperienceName.REMIX_INSERT,
			error: outcome.error,
			attributes: {
				...attributes,
				renderFailureReason: outcome.reason,
			},
		});
	} catch {
		// Reliability telemetry must never affect the inserted content's render path.
	}
};

const retireVerification = (key: string, pending: PendingVerification): void => {
	if (pendingVerifications.get(key) !== pending) {
		return;
	}
	pendingVerifications.delete(key);
	stopObserving(pending);
	if (!pending.deadlineExceeded) {
		try {
			pending.experienceTracker.abort({
				name: ExperienceName.REMIX_INSERT_LIMITED,
				reason: 'render_verification_replaced',
			});
		} catch {}
	}
};

const recordOutcome = (identity: RemixInsertRenderIdentity, outcome: RenderOutcome): void => {
	const key = toKey(identity);
	const pending = pendingVerifications.get(key);
	if (pending) {
		pendingVerifications.delete(key);
		settle(pending, outcome);
		return;
	}

	const previous = recentOutcomes.get(key);
	if (previous?.timeout !== undefined) {
		clearTimeout(previous.timeout);
	}
	const timeout =
		retainedRenderIdentities.has(key) && outcome.type === 'succeeded'
			? undefined
			: setTimeout(() => recentOutcomes.delete(key), REMIX_INSERT_RENDER_TIMEOUT_MS);
	recentOutcomes.set(key, { outcome, timeout });
};

/**
 * Transfers an already-started aiMateRemixInsert experience to the render lifecycle.
 * Render notifications may arrive before the editor insertion acknowledgement, so
 * recent outcomes are retained briefly and consumed here.
 */
export const beginRemixInsertRenderVerification = ({
	attributes,
	experienceTracker,
	identity,
	renderTimeoutMs = REMIX_INSERT_RENDER_TIMEOUT_MS,
}: {
	attributes?: Record<string, unknown>;
	experienceTracker: ExperienceTrackerAPI;
	identity: RemixInsertRenderIdentity;
	renderTimeoutMs?: number;
}): void => {
	const remixCreateId = attributes?.remixCreateId;
	if (typeof remixCreateId === 'string') {
		remixCreateTerminalOwnership.commit(remixCreateId);
	}
	const key = toKey(identity);
	for (const [previousKey, previous] of pendingVerifications) {
		retireVerification(previousKey, previous);
	}
	const renderAttemptId = crypto.randomUUID();
	const trackingAttributes = { ...attributes, renderAttemptId };
	const pending: PendingVerification = {
		attributes: trackingAttributes,
		experienceTracker,
		renderTimeoutMs,
	};
	try {
		experienceTracker.start({
			name: ExperienceName.REMIX_INSERT_LIMITED,
			id: renderAttemptId,
			attributes: { ...trackingAttributes, renderDeadlineMs: renderTimeoutMs },
		});
	} catch {}
	const recent = recentOutcomes.get(key);
	if (recent) {
		if (recent.timeout !== undefined) {
			clearTimeout(recent.timeout);
		}
		recentOutcomes.delete(key);
		clearRetainedIdentity(key);
		settle(pending, recent.outcome);
		return;
	}
	clearRetainedIdentity(key);

	try {
		pending.unsubscribe = experienceTracker.subscribe((event) => {
			if (event.action === 'taskStart' && event.name === ExperienceName.REMIX_INSERT) {
				retireVerification(key, pending);
			}
		});
	} catch {}
	pending.timeout = setTimeout(() => {
		if (pendingVerifications.get(key) !== pending) {
			return;
		}
		const error = new Error('Inserted Remix content did not render within the expected time');
		pending.deadlineExceeded = true;
		try {
			pending.experienceTracker.fail({
				name: ExperienceName.REMIX_INSERT_LIMITED,
				error,
				attributes: {
					...pending.attributes,
					renderDeadlineMs: renderTimeoutMs,
					renderFailureReason: 'render_timeout',
				},
			});
		} catch {}
	}, renderTimeoutMs);
	const observationLimitMs = Math.max(REMIX_INSERT_OBSERVATION_LIMIT_MS, renderTimeoutMs);
	pending.observationTimeout = setTimeout(() => {
		if (pendingVerifications.get(key) !== pending) {
			return;
		}
		pendingVerifications.delete(key);
		stopObserving(pending);
		try {
			pending.experienceTracker.abort({
				name: ExperienceName.REMIX_INSERT,
				reason: 'render_observation_limit',
				checkForTimeout: false,
				attributes: {
					...pending.attributes,
					renderDeadlineMs: pending.renderTimeoutMs,
					deadlineExceeded: !!pending.deadlineExceeded,
					outcome: 'timed_out',
					observationLimitMs,
				},
			});
		} catch {
			// Reliability telemetry must never affect the inserted content's render path.
		}
	}, observationLimitMs);
	pendingVerifications.set(key, pending);
};

export const completeRemixInsertRenderVerification = (identity: RemixInsertRenderIdentity): void =>
	recordOutcome(identity, { type: 'succeeded' });

export const failRemixInsertRenderVerification = ({
	error,
	identity,
	reason,
}: {
	error: Error;
	identity: RemixInsertRenderIdentity;
	reason: string;
}): void => {
	if (reason === 'render_timeout') {
		return;
	}
	recordOutcome(identity, { error, reason, type: 'failed' });
};

export const resetRemixInsertRenderVerificationsForTesting = (): void => {
	for (const pending of pendingVerifications.values()) {
		stopObserving(pending);
	}
	for (const recent of recentOutcomes.values()) {
		if (recent.timeout !== undefined) {
			clearTimeout(recent.timeout);
		}
	}
	for (const timeout of retainedRenderIdentities.values()) {
		clearTimeout(timeout);
	}
	pendingVerifications.clear();
	recentOutcomes.clear();
	retainedRenderIdentities.clear();
	remixCreateTerminalOwnership.reset();
};
